import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Refresh,
  Delete,
  Visibility,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  Email as EmailIcon,
  CloudSync,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import * as emailService from '../../services/emailService';

const EmailDebug = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useAuth();
  const [detailDialog, setDetailDialog] = useState({ open: false, email: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, email: null });
  const [mailboxDialog, setMailboxDialog] = useState({ open: false });
  const [statusFilter, setStatusFilter] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [mailboxConfig, setMailboxConfig] = useState({
    host: '',
    port: 993,
    user: '',
    password: '',
    tls: true,
    markAsRead: true,
    maxEmails: 50
  });
  const [envConfigured, setEnvConfigured] = useState(false);

  useEffect(() => {
    document.title = 'Email Debug - EchoNote';
    const loadEmailsWrapper = async () => {
      try {
        setError('');
        const filters = statusFilter ? { status: statusFilter } : {};
        const response = await emailService.getIncomingEmails(filters);
        setEmails(response.data);
      } catch (error) {
        console.error('Failed to load emails:', error);
        setError('Failed to load incoming emails');
      } finally {
        setLoading(false);
      }
    };

    loadEmailsWrapper();
    
    // Auto-refresh every 30 seconds
    // Only continue refreshing if user is still logged in
    const interval = setInterval(() => {
      if (user) {
        loadEmailsWrapper();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [statusFilter, user]);

  useEffect(() => {
    // Check if mailbox is configured via environment variables
    const checkMailboxConfig = async () => {
      try {
        const response = await emailService.getMailboxConfig();
        setEnvConfigured(response.data.configured);
        if (response.data.configured) {
          setMailboxConfig(prev => ({
            ...prev,
            host: response.data.host || prev.host,
            port: response.data.port || prev.port,
            user: response.data.user || prev.user,
            tls: response.data.tls,
            markAsRead: response.data.markAsRead,
            maxEmails: response.data.maxEmails
          }));
        }
      } catch (error) {
        console.error('Failed to check mailbox config:', error);
      }
    };
    checkMailboxConfig();
  }, []);

  const loadEmails = async () => {
    try {
      setError('');
      const filters = statusFilter ? { status: statusFilter } : {};
      const response = await emailService.getIncomingEmails(filters);
      setEmails(response.data);
    } catch (error) {
      console.error('Failed to load emails:', error);
      setError('Failed to load incoming emails');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (email) => {
    try {
      const response = await emailService.getIncomingEmail(email.id);
      setDetailDialog({ open: true, email: response.data });
    } catch (error) {
      console.error('Failed to load email details:', error);
      setError('Failed to load email details');
    }
  };

  const handleDelete = async () => {
    try {
      await emailService.deleteIncomingEmail(deleteDialog.email.id);
      setEmails(emails.filter(e => e.id !== deleteDialog.email.id));
      setDeleteDialog({ open: false, email: null });
    } catch (error) {
      console.error('Delete error:', error);
      setError('Failed to delete email record');
    }
  };

  const handleSyncMailbox = async (useEnvConfig = false) => {
    try {
      setSyncing(true);
      setError('');
      setSuccess('');
      
      // Use env config if specified, otherwise use form config
      const config = useEnvConfig ? null : mailboxConfig;
      
      const response = await emailService.syncMailbox(config);
      
      const results = response.data.results;
      if (results.processed > 0 || results.total > 0) {
        setSuccess(
          `Mailbox sync completed! Found ${results.total} email(s), ` +
          `processed ${results.processed}, failed ${results.failed}.`
        );
      } else {
        setSuccess('Mailbox sync completed. No new emails found.');
      }
      
      // Reload emails
      await loadEmails();
      
      // Close dialog
      setMailboxDialog({ open: false });
      
      // Clear success message after 10 seconds
      setTimeout(() => setSuccess(''), 10000);
    } catch (error) {
      console.error('Mailbox sync error:', error);
      const errorMsg = error.response?.data?.error || error.response?.data?.details || 'Failed to sync mailbox';
      setError(errorMsg);
    } finally {
      setSyncing(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setError('');
      setSuccess('');
      
      const response = await emailService.testMailboxConnection(mailboxConfig);
      
      if (response.data.success) {
        setSuccess(`Connection successful! Found mailboxes: ${response.data.mailboxes?.join(', ') || 'INBOX'}`);
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (error) {
      console.error('Connection test error:', error);
      const errorMsg = error.response?.data?.error || 'Connection test failed';
      setError(errorMsg);
    }
  };

  const handleMailboxConfigChange = (field, value) => {
    setMailboxConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'default', icon: <Schedule />, label: 'Pending' },
      success: { color: 'success', icon: <CheckCircle />, label: 'Success' },
      failed: { color: 'error', icon: <ErrorIcon />, label: 'Failed' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
      />
    );
  };

  const columns = [
    {
      field: 'fromAddress',
      headerName: 'From',
      width: 200,
      flex: 1
    },
    {
      field: 'subject',
      headerName: 'Subject',
      width: 250,
      flex: 1
    },
    {
      field: 'platform',
      headerName: 'Platform',
      width: 100,
      renderCell: (params) => params.value ? (
        <Chip 
          label={params.value.toUpperCase()} 
          size="small" 
          variant="outlined"
        />
      ) : '-'
    },
    {
      field: 'processingStatus',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'receivedAt',
      headerName: 'Received',
      width: 180,
      renderCell: (params) => {
        const date = new Date(params.value);
        return date.toLocaleString();
      }
    },
    {
      field: 'scheduledMeeting',
      headerName: 'Meeting Created',
      width: 130,
      renderCell: (params) => params.value ? (
        <CheckCircle color="success" fontSize="small" />
      ) : (
        <ErrorIcon color="disabled" fontSize="small" />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleViewDetails(params.row)}
            color="primary"
          >
            <Visibility />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setDeleteDialog({ open: true, email: params.row })}
            color="error"
          >
            <Delete />
          </IconButton>
        </Box>
      )
    }
  ];

  const stats = {
    total: emails.length,
    pending: emails.filter(e => e.processingStatus === 'pending').length,
    success: emails.filter(e => e.processingStatus === 'success').length,
    failed: emails.filter(e => e.processingStatus === 'failed').length
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Incoming Email Review
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Filter by Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <CloudSync />}
            onClick={() => envConfigured ? handleSyncMailbox(true) : setMailboxDialog({ open: true })}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync Mailbox'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadEmails}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 3 }}>
        This page shows all incoming emails that have been received and processed by the email service.
        Use this to debug and review which emails are being converted into scheduled meetings.
        {envConfigured && ' Mailbox sync is configured via environment variables.'}
      </Alert>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Emails
              </Typography>
              <Typography variant="h4">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" color="primary.main">
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Successful
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.success}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Failed
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.failed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Emails Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Incoming Emails
          </Typography>
          <Box sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={emails}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
            />
          </Box>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialog.open} 
        onClose={() => setDetailDialog({ open: false, email: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon />
            Email Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {detailDialog.email && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="From"
                    value={`${detailDialog.email.fromName || ''} <${detailDialog.email.fromAddress}>`}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subject"
                    value={detailDialog.email.subject || ''}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Status"
                    value={detailDialog.email.processingStatus}
                    InputProps={{ 
                      readOnly: true,
                      endAdornment: getStatusChip(detailDialog.email.processingStatus)
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Received At"
                    value={new Date(detailDialog.email.receivedAt).toLocaleString()}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                {detailDialog.email.meetingUrl && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Meeting URL"
                      value={detailDialog.email.meetingUrl}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )}
                {detailDialog.email.platform && (
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Platform"
                      value={detailDialog.email.platform}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )}
                {detailDialog.email.scheduledTime && (
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Scheduled Time"
                      value={new Date(detailDialog.email.scheduledTime).toLocaleString()}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )}
                {detailDialog.email.duration && (
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Duration"
                      value={`${detailDialog.email.duration} minutes`}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )}
                {detailDialog.email.errorMessage && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      <Typography variant="subtitle2">Error Message:</Typography>
                      <Typography variant="body2">{detailDialog.email.errorMessage}</Typography>
                    </Alert>
                  </Grid>
                )}
                {detailDialog.email.scheduledMeeting && (
                  <Grid item xs={12}>
                    <Alert severity="success">
                      <Typography variant="subtitle2">Scheduled Meeting Created:</Typography>
                      <Typography variant="body2">
                        {detailDialog.email.scheduledMeeting.title} (Status: {detailDialog.email.scheduledMeeting.botStatus})
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email Body (Text)"
                    value={detailDialog.email.bodyText || 'No text content'}
                    multiline
                    rows={6}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                {detailDialog.email.rawEmailPreview && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Raw Email Preview"
                      value={detailDialog.email.rawEmailPreview}
                      multiline
                      rows={4}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog({ open: false, email: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, email: null })}
      >
        <DialogTitle>Delete Email Record</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this email record? This will not affect any scheduled meetings that were created from it.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, email: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mailbox Configuration Dialog */}
      <Dialog
        open={mailboxDialog.open}
        onClose={() => setMailboxDialog({ open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SettingsIcon />
            Configure Mailbox Sync
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {!envConfigured && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Configure your IMAP mailbox to automatically fetch meeting invitation emails.
              </Alert>
            )}
            {envConfigured && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Mailbox is configured via environment variables. You can override settings below.
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="IMAP Host"
                  value={mailboxConfig.host}
                  onChange={(e) => handleMailboxConfigChange('host', e.target.value)}
                  placeholder="imap.gmail.com"
                  helperText="Common hosts: imap.gmail.com, outlook.office365.com"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Port"
                  type="number"
                  value={mailboxConfig.port}
                  onChange={(e) => handleMailboxConfigChange('port', parseInt(e.target.value))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mailboxConfig.tls}
                      onChange={(e) => handleMailboxConfigChange('tls', e.target.checked)}
                    />
                  }
                  label="Use TLS"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email / Username"
                  value={mailboxConfig.user}
                  onChange={(e) => handleMailboxConfigChange('user', e.target.value)}
                  placeholder="user@example.com"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={mailboxConfig.password}
                  onChange={(e) => handleMailboxConfigChange('password', e.target.value)}
                  helperText="For Gmail with 2FA, use an App Password"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Emails"
                  type="number"
                  value={mailboxConfig.maxEmails}
                  onChange={(e) => handleMailboxConfigChange('maxEmails', parseInt(e.target.value))}
                  helperText="Maximum emails to fetch per sync"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mailboxConfig.markAsRead}
                      onChange={(e) => handleMailboxConfigChange('markAsRead', e.target.checked)}
                    />
                  }
                  label="Mark as Read"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTestConnection} disabled={syncing}>
            Test Connection
          </Button>
          <Button onClick={() => setMailboxDialog({ open: false })}>
            Cancel
          </Button>
          <Button 
            onClick={() => handleSyncMailbox(false)} 
            variant="contained"
            disabled={syncing || !mailboxConfig.host || !mailboxConfig.user || !mailboxConfig.password}
          >
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailDebug;
